import React, { useEffect, useRef, useState } from 'react';
import { Button, Space, Typography, message } from 'antd';
import { uploadSimplifiedOptionHistoryAdmin } from './adminDataApi';

const CSV_TEMPLATE_CONTENT = [
  'timestamp,strike,annualized_implied_volatility,basis_points,source,source_reference',
  '2026-01-05T00:00:00Z,102.00,0.20,35.00,mock_vendor,example-curve-001',
  '2026-01-06T00:00:00Z,102.25,0.21,34.50,mock_vendor,example-curve-002',
  '',
].join('\n');
const CSV_TEMPLATE = `data:text/csv;charset=utf-8,${encodeURIComponent(
  CSV_TEMPLATE_CONTENT,
)}`;

type Props = {
  simplifiedOptionId?: number;
  onUploaded: () => Promise<void> | void;
};

/** Provides the minimal per-asset CSV upload for strike, volatility, and optional BP curves. */
const SimplifiedOptionCurveCsvUpload: React.FC<Props> = ({
  simplifiedOptionId,
  onUploaded,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File>();
  const [uploading, setUploading] = useState(false);

  // Clears the file on asset changes so it cannot reach the wrong option.
  useEffect(() => {
    setFile(undefined);
    if (inputRef.current) inputRef.current.value = '';
  }, [simplifiedOptionId]);

  /** Remembers the chosen CSV until the admin explicitly starts the upload. */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0]);
  };

  /** Uploads the CSV for the selected asset and reloads the canonical curve table on success. */
  const handleUpload = async () => {
    if (!simplifiedOptionId || !file) return;
    setUploading(true);
    try {
      const result = await uploadSimplifiedOptionHistoryAdmin(
        simplifiedOptionId,
        file,
      );
      message.success(`Imported ${result.created} curve row(s).`);
      setFile(undefined);
      if (inputRef.current) inputRef.current.value = '';
      await onUploaded();
    } catch (error) {
      message.error(
        error instanceof Error
          ? `CSV upload failed: ${error.message}`
          : `CSV upload failed: ${String(error)}`,
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Space wrap>
      <input
        ref={inputRef}
        aria-label="Simplified option curve CSV"
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileChange}
      />
      <Button
        type="primary"
        disabled={!simplifiedOptionId || !file}
        loading={uploading}
        onClick={handleUpload}
      >
        Upload CSV
      </Button>
      <Typography.Link
        href={CSV_TEMPLATE}
        download="simplified-option-curves-template.csv"
      >
        Download CSV template
      </Typography.Link>
    </Space>
  );
};

export default SimplifiedOptionCurveCsvUpload;
